import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {NormalizedSequenceString} from '@github-ui/hotkey'

/** Return a copy of the array without the first encountered instance of `value` (based on `===` comparison). */
export function filterOnce<T>(array: readonly T[], value: T) {
  let encounteredOnce = false
  return array.filter(el => {
    if (el === value && !encounteredOnce) {
      encounteredOnce = true
      return false
    }
    return true
  })
}

const ARIA_MODAL_SELECTOR = '[role="dialog"][aria-modal="true"]'

/**
 * The `:modal` pseudo-class (which matches native `<dialog>` elements opened with `showModal()`) is not available
 * everywhere: jsdom throws on it, and older Safari versions predate it. An unsupported pseudo-class makes
 * `matches` throw a `SyntaxError`, which would break all keyboard command handling. So we feature-detect it
 * once (lazily, on first use in the browser) and fall back to matching only ARIA modals where it isn't supported.
 */
let modalPseudoClassSupported: boolean | undefined
function supportsModalPseudoClass(): boolean {
  if (modalPseudoClassSupported !== undefined) return modalPseudoClassSupported

  try {
    // Probe against an empty fragment so the check parses the selector but traverses zero nodes
    document.createDocumentFragment().querySelector('dialog:modal')
    modalPseudoClassSupported = true
  } catch {
    modalPseudoClassSupported = false
  }
  return modalPseudoClassSupported
}

const isNativeModal = (element: Element) => element.localName === 'dialog' && element.matches(':modal')

/** Modal candidates in document order. Two queries are several times faster than one `dialog:modal, …` selector list. */
function getModalCandidates(): Element[] {
  const ariaModals = Array.from(document.querySelectorAll(ARIA_MODAL_SELECTOR))

  const dialogs = document.getElementsByTagName('dialog')
  if (dialogs.length === 0 || !supportsModalPseudoClass()) return ariaModals

  const nativeModals = Array.from(dialogs).filter(isNativeModal)
  if (nativeModals.length === 0) return ariaModals

  return Array.from(new Set([...ariaModals, ...nativeModals])).sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  )
}

/** Elements that DOM state alone shows aren't rendered (e.g. `<details-dialog>` in a closed `<details>`). */
const NOT_RENDERED_SELECTOR = [
  '[hidden]',
  '[hidden] *',
  // A `<dialog popover>` can be shown without the `open` attribute
  'dialog:not([open]):not([popover])',
  'dialog:not([open]):not([popover]) *',
  'details:not([open]) > :not(summary:first-of-type)',
  'details:not([open]) > :not(summary:first-of-type) *',
].join(', ')

export function getActiveModal() {
  const skipMeasuringWhenPossible = isFeatureEnabled('ui_commands_fast_modal_check')
  const modals = getModalCandidates()
  for (let i = modals.length - 1; i >= 0; i--) {
    const modal = modals[i]
    if (!modal || modal.childNodes.length === 0) continue

    if (skipMeasuringWhenPossible) {
      // A native modal dialog makes the rest of the page inert even if it's hidden, so it doesn't need a layout read
      if (supportsModalPseudoClass() && isNativeModal(modal)) return modal

      if (modal.matches(NOT_RENDERED_SELECTOR)) continue
    }

    // ARIA modals can still be hidden by CSS, which can only be detected by measuring
    if (elementHasNonZeroHeight(modal)) return modal
  }
  return null
}

export function isInsideModal(modal: Element, element?: HTMLElement | null) {
  if (!element) {
    return false
  }

  return modal.contains(element) ?? false
}

function elementHasNonZeroHeight(element: Element): boolean {
  if (element.clientHeight > 0) return true

  for (const child of element.children) {
    if (elementHasNonZeroHeight(child)) return true
  }

  return false
}

export function setsEqual(a: Set<unknown>, b: Set<unknown>) {
  if (a.size !== b.size) return false
  for (const el of a) if (!b.has(el)) return false
  return true
}

/**
 * Iterate over the subsequences in a sequence of keys. A subsequence is a sequence with any number of initial keys
 * omitted. Each subsequence is a candidate for a hotkey match.
 *
 * Example: `[Meta, Meta+x, x]` will yield `Meta Meta+x x`, `Meta+x x`, and `x`.
 */
export function getSubsequences(keys: readonly string[]) {
  const subsequences: NormalizedSequenceString[] = []
  for (let i = 0; i < keys.length; i++) subsequences.push(keys.slice(i).join(' ') as NormalizedSequenceString)
  return subsequences
}

export type ScopeType = 'scoped' | 'global'
